import * as React from "react";
import * as Icons from "lucide-react";

const toPascalCase = (name: string) =>
  name
    .replace(/[-_ ]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

/**
 * Resolve a CMS-authored icon name (e.g. "circle-question-mark", "Zap") to a
 * lucide-react component. Tolerates kebab/snake/space casing and a few common
 * aliases/suffix variations. Returns undefined when no match is found.
 */
export const resolveLucideIcon = (
  rawName?: string,
): React.ComponentType<React.SVGProps<SVGSVGElement>> | undefined => {
  if (!rawName) return undefined;
  const aliases: Record<string, string[]> = {
    'circle-question-mark': ['CircleHelp', 'HelpCircle'],
    'question-circle': ['CircleHelp', 'HelpCircle'],
    'question': ['CircleHelp', 'HelpCircle'],
    'help': ['CircleHelp', 'HelpCircle'],
  };
  const normalized = rawName.trim().toLowerCase();
  const candidates: string[] = [];
  if (aliases[normalized]) candidates.push(...aliases[normalized]);
  const pascal = toPascalCase(rawName);
  candidates.push(pascal);
  // Try common suffix/prefix variations
  if (!/Circle$/i.test(pascal)) candidates.push(`${pascal}Circle`);
  if (/Circle$/i.test(pascal)) candidates.push(pascal.replace(/Circle$/i, ''));

  const dict = Icons as unknown as Record<
    string,
    React.ComponentType<React.SVGProps<SVGSVGElement>>
  >;
  for (const key of candidates) {
    if (dict[key]) return dict[key];
  }
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`[lucideIcon] Unknown icon: "${rawName}". Tried: ${candidates.join(', ')}`);
  }
  return undefined;
};
